import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserRound, ShieldCheck, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const ROLE_FR = { manager: "Manager", server: "Serveur / Caissier", kitchen: "Cuisine", owner: "Propriétaire" };
const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"; } catch { return "—"; } };

function Row({ label, value, testId }) {
  return (
    <div className="flex justify-between gap-3 py-2 border-b border-[#132420] last:border-0">
      <span className="text-[11px] uppercase tracking-wide text-stone-500 shrink-0">{label}</span>
      <span data-testid={testId} className="text-sm text-stone-100 text-right truncate">{value || "—"}</span>
    </div>
  );
}

function PinField({ label, value, onChange, max, testId }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-stone-500">{label}</span>
      <Input data-testid={testId} type="password" inputMode="numeric" autoComplete="off" maxLength={max} value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, max))}
        className="mt-1 bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl h-11 font-mono tracking-[0.4em]" />
    </label>
  );
}

// Mon profil : fiche en lecture seule + Sécurité (changement de PIN avec vérification du PIN actuel côté serveur).
export function MyProfile({ open, onClose }) {
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("profile");
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setTab("profile"); setCur(""); setNw(""); setCf(""); api.profile().then(setP).catch(() => setP(null)); } }, [open]);

  const max = p?.pin_len || 6;
  const canSubmit = cur.length === max && nw.length === max && cf.length === max && !busy;
  const submit = async () => {
    if (nw !== cf) { toast.error("Les deux nouveaux PIN ne correspondent pas"); setCf(""); return; }
    setBusy(true);
    try { await api.changeOwnPin(cur, nw, cf); toast.success("PIN personnel modifié"); setCur(""); setNw(""); setCf(""); setTab("profile"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Erreur"); setCur(""); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="my-profile-dialog" className="max-w-sm rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100">
        <DialogTitle className="text-lg flex items-center gap-2"><UserRound className="w-5 h-5 text-[#C69C4A]" /> Mon profil</DialogTitle>
        <DialogDescription className="text-stone-400 text-xs">Nom, rôle et autorisations sont gérés par le Manager. Votre PIN n'est visible par personne.</DialogDescription>
        <div className="flex rounded-full bg-[#111F1A] border border-[#1B3028] p-0.5 text-xs font-bold">
          <button data-testid="my-profile-tab-profile" onClick={() => setTab("profile")} className={`flex-1 rounded-full py-2 ${tab === "profile" ? "bg-[#C69C4A] text-[#0C1713]" : "text-stone-300"}`}>Profil</button>
          <button data-testid="my-profile-tab-security" onClick={() => setTab("security")} className={`flex-1 rounded-full py-2 ${tab === "security" ? "bg-[#C69C4A] text-[#0C1713]" : "text-stone-300"}`}>Sécurité</button>
        </div>
        {tab === "profile" ? (
          <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] px-3">
            <Row label="Nom" value={p?.name} testId="my-profile-name" />
            <Row label="Identifiant" value={<span className="font-mono text-[#C69C4A]">{p?.employee_code || "—"}</span>} testId="my-profile-code" />
            <Row label="Rôle" value={ROLE_FR[p?.role] || p?.role} testId="my-profile-role" />
            <Row label="Restaurant" value={p?.restaurant_name || p?.restaurant_slug} testId="my-profile-restaurant" />
            <Row label="Statut" value={p ? (p.active === false ? "Désactivé" : "Actif") : "—"} testId="my-profile-status" />
            <Row label="Dernière connexion" value={fmt(p?.last_login_at)} testId="my-profile-last-login" />
          </div>
        ) : (
          <div className="space-y-3">
            <PinField label="PIN actuel" value={cur} onChange={setCur} max={max} testId="my-profile-pin-current" />
            <PinField label="Nouveau PIN" value={nw} onChange={setNw} max={max} testId="my-profile-pin-new" />
            <PinField label="Confirmer le nouveau PIN" value={cf} onChange={setCf} max={max} testId="my-profile-pin-confirm" />
            <button data-testid="my-profile-pin-submit" onClick={submit} disabled={!canSubmit}
              className="w-full h-11 rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-bold disabled:opacity-40 flex items-center justify-center gap-2">
              <KeyRound className="w-4 h-4" /> {busy ? "…" : "Modifier mon PIN"}
            </button>
            <p className="text-[11px] text-stone-500 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Vérifié côté serveur, chiffré, jamais affiché au Manager. PIN oublié : demandez une réinitialisation au Manager.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
